import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { errorMessage, useMintToken, type TokenScope } from "./api";
import { ARIA_DISABLED_LOOK, DIALOG_CLOSE_TARGET, TAP_ON_PHONE } from "./classes";

/** Name and scopes, then the new token, shown once. */
export function MintTokenDialog({
  open,
  onOpenChange,
  scopes,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scopes: TokenScope[];
}) {
  const [name, setName] = useState("");
  const [chosen, setChosen] = useState<string[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const mint = useMintToken();
  // Create goes away when the token shows: focus moves to Copy.
  const copyButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (token) copyButton.current?.focus();
  }, [token]);

  const close = (next: boolean) => {
    if (!next) {
      // The raw token lives only here; it goes with the dialog.
      setName("");
      setChosen([]);
      setToken(null);
      setCopied(false);
      mint.reset();
    }
    onOpenChange(next);
  };

  const ready = Boolean(name.trim()) && chosen.length > 0 && !mint.isPending;

  const toggle = (id: string, on: boolean) =>
    setChosen((current) => (on ? [...current, id] : current.filter((scope) => scope !== id)));

  const create = () =>
    mint.mutate(
      { name: name.trim(), scopes: chosen },
      { onSuccess: (created) => setToken(created.token) }
    );

  const copy = async () => {
    if (!token) return;
    await navigator.clipboard.writeText(token);
    setCopied(true);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className={cn("sm:max-w-md", DIALOG_CLOSE_TARGET)}
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>New MCP access token</DialogTitle>
        </DialogHeader>
        {token ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-2">
              <code
                className="min-w-0 flex-1 select-all break-all rounded-md border bg-muted px-3 py-2 font-mono text-sm"
                aria-label="Access token"
              >
                {token}
              </code>
              <Button
                ref={copyButton}
                variant="outline"
                size="icon"
                className="shrink-0 max-sm:h-10 max-sm:w-10"
                onClick={copy}
                aria-label="Copy token"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">You won't see this token again.</p>
            <p role="status" className="sr-only">
              {copied ? "Token copied" : ""}
            </p>
            <DialogFooter>
              <Button className={TAP_ON_PHONE} onClick={() => close(false)}>
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="token-name">Name</Label>
              <Input
                id="token-name"
                value={name}
                maxLength={100}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-3">
              <Label>Scopes</Label>
              {scopes.map((scope) => {
                const checked = chosen.includes(scope.id);
                return (
                  <div key={scope.id} className="flex flex-col gap-1">
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(on) => toggle(scope.id, on === true)}
                      />
                      {scope.label}
                    </label>
                    {checked && scope.warning && (
                      <p className="flex items-start gap-2 pl-6 text-sm text-muted-foreground">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        {scope.warning}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            {mint.isError && (
              <p role="alert" className="text-xs text-destructive">
                {errorMessage(mint.error)}
              </p>
            )}
            <DialogFooter>
              <Button
                className={cn(TAP_ON_PHONE, ARIA_DISABLED_LOOK)}
                aria-disabled={!ready}
                onClick={() => {
                  if (ready) create();
                }}
              >
                Create
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
